import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { latLngBounds } from 'leaflet';
import { MapPinIcon, PlayIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { formatViews, videoThumbnail, videoUrl } from '../utils/video';
import type { MappedVideo, Region } from '../types/video';
type Filter = 'all' | Region;

// CARTO Basemaps key (free tier, keep the attribution visible)
const CARTO_BASEMAPS_KEY = 'cb1_4api_1_a29a4ed813270201e88fe51f';
const filters: {
  id: Filter;
  label: string;
}[] = [{
  id: 'ukraine',
  label: 'Україна'
}, {
  id: 'world',
  label: 'За кордоном'
}, {
  id: 'all',
  label: 'Усі'
}];
interface RouteMapProps {
  videos: MappedVideo[];
}
export function RouteMap({
  videos
}: RouteMapProps) {
  const [filter, setFilter] = useState<Filter>('ukraine');
  const [selectedId, setSelectedId] = useState(videos.find(v => v.region === 'ukraine')?.id);
  const visible = useMemo(() => filter === 'all' ? videos : videos.filter(v => v.region === filter), [filter, videos]);
  const selected = visible.find(v => v.id === selectedId) ?? visible[0];
  const changeFilter = (next: Filter) => {
    setFilter(next);
    const first = next === 'all' ? videos[0] : videos.find(v => v.region === next);
    if (first) setSelectedId(first.id);
  };
  return <section id="map" aria-labelledby="map-title" className="scroll-mt-20 border-y border-line bg-surface py-20 md:py-28" data-id="element-82" data-mp="1u5fg8huthq3i">
      <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-83" data-mp="1p7x46ili6ura">
        <SectionHeading id="map-title" title="Мапа маршрутів" subtitle="Оберіть вершину на мапі — і дивіться похід саме туди." data-id="element-84" data-mp="1k7rjxnhkcaj8" />

        <div role="tablist" aria-label="Регіон" className="mt-8 inline-flex rounded-full border border-line bg-ink p-1" data-id="element-85" data-mp="1fbkc1p5lwoyn">
          {filters.map(f => <button key={f.id} role="tab" aria-selected={filter === f.id} onClick={() => changeFilter(f.id)} className={`h-9 whitespace-nowrap rounded-full px-4 text-sm font-bold transition-colors duration-150 ${filter === f.id ? 'bg-paper text-ink' : 'text-muted hover:text-paper'}`} data-id="element-86" data-mp="3szunjfprje5">
              {f.label}
            </button>)}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-12" data-id="element-87" data-mp="28bh4aj9vyxb6">
          <div className="relative isolate z-0 h-[380px] overflow-hidden rounded-2xl border border-line md:h-[540px] lg:col-span-8" data-id="element-88" data-mp="289w7so40h4ao">
            <MapContainer center={[48, 20]} zoom={3} scrollWheelZoom={false} className="h-full w-full" attributionControl data-id="element-89" data-mp="1zbkluze3auok">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attribution/">CARTO</a>' url={`https://basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{r}.png?key=${CARTO_BASEMAPS_KEY}`} maxZoom={20} data-id="element-90" data-mp="1pcmpxbec3yvj" />
              <FitToVideos videos={visible} data-id="element-91" data-mp="16r71hpprtad4" />
              {visible.map(video => {
              const active = video.id === selected?.id;
              return <CircleMarker key={video.id} center={[video.lat, video.lng]} radius={active ? 11 : 7} pathOptions={{
                color: active ? '#F3EFE6' : '#FF6B3D',
                weight: active ? 3 : 2,
                fillColor: '#FF6B3D',
                fillOpacity: active ? 1 : 0.7
              }} eventHandlers={{
                click: () => setSelectedId(video.id)
              }} data-id="element-92" data-mp="1fnxxk24bop7p">
                    <Tooltip direction="top" offset={[0, -8]} className="route-tooltip" data-id="element-93" data-mp="2dawumdyxfz64">
                      {video.place}
                    </Tooltip>
                  </CircleMarker>;
            })}
            </MapContainer>
          </div>

          <aside aria-label="Обраний маршрут" className="flex flex-col lg:col-span-4" data-id="element-94" data-mp="apb11a1uogf5">
            {selected && <a href={videoUrl(selected)} target="_blank" rel="noopener noreferrer" className="group block rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ember" data-id="element-95" data-mp="fi45rxxbjor8">
                <div className="relative aspect-video overflow-hidden rounded-2xl" data-id="element-96" data-mp="3nz8edfbqgjjk">
                  <img src={videoThumbnail(selected)} alt="" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-97" data-mp="3sunt09hkejh1" />
                  <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true" data-id="element-98" data-mp="1d4ui5vpk6p3w">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ember text-ember-ink transition-transform duration-150 ease-out group-hover:scale-105" data-id="element-99" data-mp="2xnn3f1kednlf">
                      <PlayIcon className="h-6 w-6 translate-x-0.5 fill-current" data-id="element-100" data-mp="2nx29hrfo578u" />
                    </span>
                  </span>
                </div>
                <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ember" data-id="element-101" data-mp="2ixpmn6oss069">
                  <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-102" data-mp="2e0h3p3vtrs3y" />
                  {selected.place}, {selected.country}
                </p>
                <h3 className="mt-2 font-display text-xl font-semibold leading-snug text-paper" data-id="element-103" data-mp="24y7rm9kwrqog">
                  {selected.title}
                </h3>
                <p className="mt-2 text-sm text-muted" data-id="element-104" data-mp="2017ni47mcxzz">
                  {selected.duration} · {formatViews(selected.views)}
                </p>
              </a>}

            <div className="mt-8 border-t border-line pt-5" data-id="element-105" data-mp="3uefjw0b8thnq">
              <p className="text-sm font-semibold text-muted" data-id="element-106" data-mp="1soi6r1s56gfo">Інші місця · {visible.length}</p>
              <ul className="mt-3 flex flex-wrap gap-2" data-id="element-107" data-mp="1xeykkmzvk42k">
                {visible.map(video => <li key={video.id} data-id="element-108" data-mp="1iqasiqp8mchd">
                    <button onClick={() => setSelectedId(video.id)} aria-pressed={video.id === selected?.id} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors duration-150 ${video.id === selected?.id ? 'border-ember bg-ember text-ember-ink' : 'border-line text-paper/85 hover:border-paper/40'}`} data-id="element-109" data-mp="1irdi8vatq8vp">
                      {video.place}
                    </button>
                  </li>)}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}
function FitToVideos({
  videos
}: {
  videos: MappedVideo[];
}) {
  const map = useMap();
  useEffect(() => {
    if (videos.length === 0) return;
    const bounds = latLngBounds(videos.map(v => [v.lat, v.lng] as [number, number]));
    map.fitBounds(bounds, {
      padding: [48, 48],
      maxZoom: 7
    });
  }, [videos, map]);
  return null;
}